import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DemoBadge, PageHeader, StatTiles } from '@/components/PageHeader'
import { fetchRollup } from '@/features/planning/api'
import { DivisionRollup } from '@/features/portfolio/DivisionRollup'
import { StalledTable } from '@/features/portfolio/StalledTable'
import { usePortfolio } from '@/features/portfolio/usePortfolio'
import { fetchWorkflows } from '@/features/workflow/api'
import type { ApiError } from '@/lib/api/client'
import type { StateCategory } from '@/types/domain'
import type { PortfolioRollup } from '@/types/planning'
import type { Workflow } from '@/types/workflow'
import { Icon } from '@/components/Icon'
import { useSession } from '@/features/auth/useSession'
import { NotificationList } from '@/features/notifications/NotificationList'
import { NewProjectDialog } from '@/features/portfolio/NewProjectDialog'

const CATEGORY_CLASS: Record<StateCategory, string> = {
  backlog: 'text-ink-500 border-line',
  started: 'text-brand-600 border-brand-300 bg-brand-50',
  blocked: 'text-[color:var(--color-state-blocked)] border-[color:var(--color-state-blocked)]',
  done: 'text-[color:var(--color-state-done)] border-[color:var(--color-state-done)]',
  cancelled: 'text-ink-500 border-line',
}

const THRESHOLDS = [5, 10, 20] as const

/**
 * The portfolio dashboard: all five divisions rolled into one view, and every
 * stalled engagement in the group listed beneath them. This is the screen
 * Phase 2 exists to produce.
 */
export function PortfolioPage() {
  const { t } = useTranslation()
  const session = useSession()
  const [threshold, setThreshold] = useState<number>(10)
  const { isLive, isPending, divisions, summary } = usePortfolio()

  const rollup = useQuery<PortfolioRollup, ApiError>({
    queryKey: ['portfolio-rollup', threshold],
    queryFn: () => fetchRollup(threshold),
    retry: false,
  })

  // The pipeline's stages come from the engagement workflow itself, not from a
  // bundled copy. That is both more honest — a division that renames a stage
  // sees the new name here — and the only version a guest cannot read, since
  // the workflow endpoint refuses them.
  const workflows = useQuery<Workflow[], ApiError>({
    queryKey: ['workflows'],
    queryFn: fetchWorkflows,
    retry: false,
  })

  const engagement = workflows.data?.find((workflow) => workflow.appliesTo === 'project')

  const byStage = (engagement?.states ?? []).map((state) => ({
    name: state.name,
    category: state.category,
    count: summary.byStage[state.name] ?? 0,
  }))

  const [creating, setCreating] = useState(false)

  const tiles = [
    { label: t('portfolio.divisions'), value: summary.divisions },
    { label: t('portfolio.projects'), value: summary.projects },
    { label: t('portfolio.clients'), value: summary.clients },
    // Guests get no headcount, so the tile is absent rather than blank.
    ...(summary.people === null ? [] : [{ label: t('portfolio.people'), value: summary.people }]),
  ]

  if (isPending) {
    return <p className="text-ink-500 font-mono text-xs">{t('common.loading')}</p>
  }

  return (
    <div>
      <PageHeader
        title={t('portfolio.title')}
        subtitle={t('portfolio.subtitle', {
          count: divisions.length,
          organization: session.data?.organization.name ?? '',
        })}
        action={
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="bg-brand-600 flex items-center gap-1.5 rounded px-3 py-1.5 text-[13px] font-medium text-white"
          >
            <Icon name="plus" className="size-3.5" />
            {t('portfolio.newProject')}
          </button>
        }
      />
      <DemoBadge label={isLive ? t('common.liveData') : t('common.demoData')} />

      <StatTiles items={tiles} />

      {/* The home page answers "what needs me" before "how is everything" —
          which is the order someone opening the product actually asks in. */}
      <section className="mb-9">
        <h2 className="text-ink-900 mb-3 text-sm font-semibold">{t('notifications.title')}</h2>
        <div className="border-line bg-surface overflow-hidden rounded-md border">
          <NotificationList limit={6} />
        </div>
      </section>

      <section className="mb-9" hidden={byStage.length === 0}>
        <h2 className="text-ink-900 mb-3 text-sm font-semibold">{t('portfolio.pipeline')}</h2>
        <ol className="flex flex-wrap items-center gap-2">
          {byStage.map((stage) => (
            <li
              key={stage.name}
              // Flex with a gap rather than a margin, so the spacing survives
              // the right-to-left flip.
              className={`bg-surface inline-flex items-center gap-2 rounded border px-2.5 py-1.5 font-mono text-xs ${CATEGORY_CLASS[stage.category]}`}
            >
              <span>{stage.name}</span>
              <span className="tabular-nums opacity-70">{stage.count}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="mb-9">
        <h2 className="text-ink-900 mb-3 text-sm font-semibold">{t('portfolio.rollup')}</h2>
        <DivisionRollup divisions={rollup.data?.divisions ?? []} />
      </section>

      <section>
        <div className="mb-3 flex flex-wrap items-baseline gap-x-4 gap-y-2">
          <h2 className="text-ink-900 text-sm font-semibold">
            {t('portfolio.stalledTitle')}
            <span className="text-ink-500 ms-2 font-mono text-xs tabular-nums">
              {rollup.data?.totals.stalled ?? 0}
            </span>
          </h2>

          <div className="ms-auto flex items-center gap-1.5">
            <span className="text-ink-500 font-mono text-[10px] tracking-widest uppercase">
              {t('portfolio.threshold')}
            </span>
            {THRESHOLDS.map((days) => (
              <button
                key={days}
                type="button"
                onClick={() => setThreshold(days)}
                aria-pressed={threshold === days}
                className={`rounded border px-2 py-0.5 font-mono text-xs tabular-nums ${
                  threshold === days
                    ? 'border-brand-600 bg-brand-600 text-white'
                    : 'border-line text-ink-500 hover:bg-surface-2'
                }`}
              >
                {days}
              </button>
            ))}
            <span className="text-ink-500 text-xs">{t('portfolio.workingDays')}</span>
          </div>
        </div>

        <p className="text-ink-700 mb-3 max-w-3xl text-sm">
          {t('portfolio.stalledExplainer', { count: rollup.data?.thresholdDays ?? threshold })}
        </p>

        <StalledTable rows={rollup.data?.stalled ?? []} />
      </section>
      {creating ? <NewProjectDialog onClose={() => setCreating(false)} /> : null}
    </div>
  )
}
